import React, { useContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';

import { useLocation, useNavigate, createSearchParams } from 'react-router-dom';

import getI18nService from '../../i18n/i18n';
import { Context } from '../../store/Store';
import { callFetchData } from '../../common/Fetch';
import { emailRegex } from '../../common/Validation';
import userService from '../../api/UserService';
import { setStorageItem } from '../../common/Storage';
import GuestButton from '../includes/GuestButton';
import TermsAndConditions from '../includes/TermsAndConditions';

const i18n = getI18nService();
const env = window['USERSERVICE_ENV'] || 'dev';
/*
 * Quill editor formats
 * See https://quilljs.com/docs/formats/
 */
const modules = {
  toolbar: false,
  clipboard: {
    matchVisual: false,
  },
};

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const SPONSOR_EMAIL = 'SPONSOR_EMAIL';
const TNC = 'TNC';

const widths = {
  [TNC]: '80%',
  [SPONSOR_EMAIL]: 500,
};
const heights = {
  [TNC]: 700,
  [SPONSOR_EMAIL]: 500,
};
const portalTypes = {
  SPONSOR_EMAIL,
  TNC,
};

const label = { inputProps: { 'aria-label': 'Terms and conditions' } };
const forwardTimeout = 1000;

const TermsSponsor = props => {
  const theme = useTheme();
  const { portalData } = props;
  let query = useQuery();
  const navigate = useNavigate();
  const [state, dispatch] = useContext(Context);
  const { ui } = state;
  const { customization } = ui || {};
  const [editorHtml, setEditorHtml] = useState(null);
  const [emailData, setEmailData] = useState({
    email: '',
    name: '',
    sponsorName: '',
    sponsorEmail: '',
  });
  const [showTncForEmail, setShowTncForEmail] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [validation, setValidation] = useState({
    name: true,
    email: true,
    sponsorEmail: true,
    acceptTerms: false,
  });
  const [isEmailRequestValid, setIsEmailRequestValid] = useState(false);
  // const isHandheld = useMediaQuery('(min-width:600px)');
  const [helperText, setHelperText] = useState({
    email: '',
    name: '',
    sponsorName: '',
    sponsorEmail: '',
  });
  const [showSponsor, setShowSponsor] = useState(true);

  const termsEl = useRef(null);
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.grey[700],
      },
    },
  };

  useEffect(() => {
    try {
      let content = portalData.tnc;
      if (typeof content === 'string' && content.startsWith('{')) {
        content = JSON.parse(portalData.tnc);
      }
      setEditorHtml(content);
    } catch (error) {
      setEditorHtml(portalData.tnc);
      console.error(error);
    }
  }, [portalData]);

  useEffect(() => {
    if (portalData?.sponsorListEnabled) {
      setShowSponsor(false);
    }
  }, [portalData]);

  const logQuery = () => {
    console.log(
      `${query.get('cmd')} ${query.get('essid')} ${query.get('forward')} ${query.get('ip')} ${query.get(
        'mac'
      )} ${query.get('sessionId')} `
    );
  };

  useEffect(() => {
    logQuery();
  }, []);

  const validateSponsorAsync = async (key, email) => {
    const sessionId = query.get('sessionId');
    // const authenticated = true;
    const api = Object.assign({}, userService.ValidateSponsorEmail, {
      data: { sessionId, email: email.toLowerCase() },
    });
    const result = await callFetchData(api);
    if (result?.status === 200) {
      setHelperText(Object.assign({}, helperText, { [key]: '' }));
      // SW-43584 change and blur events along with async vaidation cause issues to bypass these
      // had to use an actual dom node for checkbox with an uncontrolled Checkbox
      const termsChkd = termsEl.current && termsEl.current.firstChild ? termsEl.current.firstChild.checked : false;
      syncValidation({ [key]: true }, termsChkd);
    } else if (result?.status === 404) {
      setHelperText(
        Object.assign({}, helperText, {
          [key]: `Email domain ${email.split('@')[1]} is not allowed as sponsor's email domain.`,
        })
      );
      syncValidation({ [key]: false });
    }
  };

  const validateSponsor = (key, email) => {
    const { emailDomains = [] } = portalData;
    const domain = email.split('@')[1];
    const found = emailDomains.find(d => d.toLowerCase() === domain.toLowerCase());
    if (found) {
      setHelperText(Object.assign({}, helperText, { [key]: '' }));
      syncValidation({ [key]: true });
    } else {
      setHelperText(
        Object.assign({}, helperText, {
          [key]: `Email domain ${domain} is not allowed as sponsor's email domain.`,
        })
      );
      syncValidation({ [key]: false });
    }
  };

  const syncValidation = (change, acceptTerms) => {
    const updates = Object.assign({}, validation, change);
    if (acceptTerms) {
      updates.acceptTerms = true;
    }
    const values = Object.values(updates);
    let reqValid = true;
    if (values.indexOf(false) > -1) {
      reqValid = false;
    }
    if (emailData.email.length === 0 || (showSponsor && emailData.sponsorEmail.length === 0)) {
      reqValid = false;
    }
    setIsEmailRequestValid(reqValid);
    setValidation(updates);
    console.log(`reqValid: ${reqValid}`);
  };
  const handleValidation = (key, value) => {
    if (key === 'email' || key === 'sponsorEmail') {
      const res = emailRegex.test(value);
      syncValidation({ [key]: res });
      if (!res) {
        setHelperText(Object.assign({}, helperText, { [key]: 'Invalid email' }));
      } else {
        setHelperText(Object.assign({}, helperText, { [key]: '' }));
        if (key === 'sponsorEmail') {
          console.log('Start server based sponsor validation');
          console.log(location.pathname + location.search);
          validateSponsor(key, value);
        }
      }
    }
    if (key === 'name') {
      if (value.length > 0) {
        syncValidation({ [key]: true });
        setHelperText(Object.assign({}, helperText, { [key]: '' }));
      } else {
        syncValidation({ [key]: false });
        setHelperText(Object.assign({}, helperText, { [key]: 'Invalid name' }));
      }
    }
    if (key === 'acceptTerms') {
      syncValidation({ [key]: value });
    }
  };

  const handleRequestAccess = async () => {
    const { email, name, sponsorName, sponsorEmail } = emailData;
    const nameSplit = name.split(' ');
    const sponsorNameSplit = sponsorName.split(' ');
    const guestFirstName = nameSplit[0];
    const guestLastName = nameSplit.slice(1).join(' ');
    const guestEmail = email;
    const sponsorFirstName = sponsorNameSplit[0];
    const sponsorLastName = sponsorNameSplit.slice(1).join(' ');
    const portalType = 'SPONSOR_EMAIL';
    const payload = {
      guestEmail,
      guestFirstName,
      guestLastName,
      sponsorFirstName,
      sponsorLastName,
      sponsorEmail: sponsorEmail.toLowerCase(),
      portalType,
    };
    console.log('About to send a request to get email approval with following payload');
    console.log(JSON.stringify(payload));
    const sessionId = query.get('sessionId');
    const essid = query.get('essid');
    const forward = query.get('forward');
    const mac = query.get('mac');
    const ip = query.get('ip');
    const api = Object.assign({}, userService.UserLogin, {
      data: { sessionId },
      params: { essid, forward, mac, ip },
      body: payload,
    });
    // save privious url for start over in error scenario
    const initialUrl = location.pathname + location.search;

    const result = await callFetchData(api);
    if (result && result.status <= 204) {
      console.log(`Successfully submitted the request.`);
      // add to storage for later use.
      setStorageItem(api.name, payload);
      dispatch({
        type: 'emailData',
        payload,
      });
      const sessionId = query.get('sessionId');
      navigate({
        pathname: '/checkRequestStatus',
        search: createSearchParams({
          sessionId,
        }).toString(),
      });
    } else {
      console.log(`Failed to submit the request for ${guestEmail}`);
      dispatch({
        type: 'initialUrl',
        initialUrl,
      });
      navigate('/error');
    }
  };

  const showTermsDialog = event => {
    console.log('SHOW NOW');
    setShowTncForEmail(true);
  };
  const hideTermsDialog = event => {
    console.log('SHOW NOW');
    setShowTncForEmail(false);
  };

  const handleChange = event => {
    const { name, value } = event.target;
    const change = { [name]: value };
    setEmailData(Object.assign({}, emailData, change));
    handleValidation(name, value);
  };
  const handleTermsAcceptChange = event => {
    const { checked } = event.target;
    setAcceptTerms(checked);
    handleValidation('acceptTerms', checked);
  };

  const getEmailApproval = () => {
    return (
      <Stack direction="column" spacing={3} sx={{ padding: theme.spacing(2, 4) }}>
        <TextField
          name="name"
          id="test_guest_sponsor_inputName"
          label={i18n.t('Vocabulary.name')}
          value={emailData.name}
          variant="outlined"
          size="small"
          onChange={handleChange}
          // onBlur={handleOnBlur}
          error={!validation['name']}
          helperText={helperText['name']}
          sx={fieldSx}
        />
        <TextField
          name="email"
          id="test_guest_sponsor_inputEmail"
          label={i18n.t('Vocabulary.email')}
          value={emailData.email}
          variant="outlined"
          size="small"
          onChange={handleChange}
          // onBlur={handleOnBlur}
          error={!validation['email']}
          helperText={helperText['email']}
          sx={fieldSx}
        />
        {showSponsor && (
          <>
            <TextField
              name="sponsorName"
              label={i18n.t('Vocabulary.sponsorName')}
              id="test_guest_sponsor_inputSponsorName"
              value={emailData.sponsorName}
              variant="outlined"
              size="small"
              onChange={handleChange}
              sx={fieldSx}
            />
            <TextField
              name="sponsorEmail"
              label={i18n.t('Vocabulary.sponsorEmail')}
              id="test_guest_sponsor_inputSponsorEmail"
              value={emailData.sponsorEmail}
              variant="outlined"
              size="small"
              onChange={handleChange}
              // onBlur={handleOnBlur}
              error={!validation['sponsorEmail']}
              helperText={helperText['sponsorEmail']}
              sx={fieldSx}
            />
          </>
        )}
      </Stack>
    );
  };

  const labelTermsCheckBox = () => {
    return (
      <Typography>
        {i18n.t('Messages.agreeTerms')}
        <Link
          href="#"
          underline="none"
          sx={{ pl: 1, color: theme.palette.primary.main || 'primary.main', fontWeight: 400 }}
          variant="subtitle1"
          onClick={showTermsDialog}
        >
          {i18n.t('Messages.termsOfService')}
        </Link>
      </Typography>
    );
  };

  return (
    <Box
      id="test_guest_sponsor"
      sx={{
        minWidth: 400,
        maxWidth: portalData ? widths[portalData.portalType] : widths['TNC'],
        // maxHeight: 720,
        mx: 'auto',
        opacity: 0.9,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            maxHeight: 700,
            minWidth: 400,
            color: 'text.primary',
          }}
        >
          <>
            {!showTncForEmail && getEmailApproval()}
            {showTncForEmail && (
              <TermsAndConditions editorHtml={editorHtml} withClose={true} onClose={hideTermsDialog} />
            )}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                p: 1,
              }}
            >
              {!showTncForEmail && (
                <Box sx={{ mb: 2 }}>
                  <FormGroup>
                    <FormControlLabel
                      id="test_guest_sponsor_acceptTermsCheckBox"
                      control={
                        <Checkbox
                          id="test_guest_sponsor_acceptCheckBox"
                          sx={{
                            color: customization.palettes[0].background.accent || theme.palette.text.secondary,
                            '&.Mui-checked': {
                              color: customization.palettes[0].background.accent || theme.palette.primary.main,
                            },
                          }}
                          checked={acceptTerms}
                          onChange={handleTermsAcceptChange}
                          {...label}
                          ref={termsEl}
                        />
                      }
                      label={labelTermsCheckBox()}
                    />
                  </FormGroup>
                </Box>
              )}
              <Stack direction="column" justifyContent="center" alignItems="center" spacing={2} sx={{ p: 2 }}>
                <GuestButton
                  id="test_guest_sponsor_reqAccessBtn"
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    width: 'fit-content',
                  }}
                  disabled={!isEmailRequestValid}
                  onClick={handleRequestAccess}
                >
                  {i18n.t('Vocabulary.requestAccess')}
                </GuestButton>
                <Alert variant="outlined" severity="info">
                  <Typography sx={{ color: theme.palette.info.main || theme.palette.text.secondary }} variant="body2">
                    {i18n.t('Messages.emailApprovalTitle')}
                  </Typography>
                </Alert>
              </Stack>
            </Box>
          </>
        </Box>
      </Box>
    </Box>
  );
};

TermsSponsor.propTypes = {
  portalData: PropTypes.object.isRequired,
};

export default TermsSponsor;
